/**
 * The conversational partner's pronoun (D235) — the read half: what "her"
 * means to the parser while the player is talking with her.
 *
 * @remarks
 * Two things the parser receives, both derived from `session.conversationPartnerId`
 * (the write half is `execution/conversational-partner.ts`) and both only
 * while the partner is still addressable from where the player stands:
 *
 * - **The antecedent map** — the partner's declared `pronoun` in its object
 *   form (`she` → `her`) naming the partner's ref, which the parser puts at
 *   the head of a clause's recency view when that word is the clause's one
 *   pronoun.
 * - **A catalog in which that word is the partner's alone.** The parser reads
 *   a pronoun that is also a declared word as that word first (D226, E12:
 *   `him` among Sylvain's nouns), so a thing in the room that lists `her`
 *   among its aliases — the projected frame, the woman on the second table —
 *   would take `her` from the woman behind the door. While she is the one
 *   being talked with, the word is hers: every other entry's `her` is set
 *   aside for this parse, and comes back the moment she is not.
 *
 * An actor that declares no pronoun contributes nothing; recency and the
 * declared-word reading stand exactly as they were.
 *
 * @packageDocumentation
 */

import type { ActorPronoun, RuntimeState } from '@orpheus/content-schema';
import type { ParseCandidateEntity, PronounAntecedents, PronounWord } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor } from '../bundle/bundle-lookups.js';
import { isActorAddressable } from './actor-presence.js';
import { entityRefForActor } from './entity-ref.js';

/**
 * The object form the player types for each declared pronoun — `she` is
 * addressed as `her`. D245: the actor's catalog entry carries it too, so the
 * parser lets only that word reach the body (`it` never means her).
 */
export const OBJECT_FORM_OF: Readonly<Record<ActorPronoun, PronounWord>> = { he: 'him', it: 'it', she: 'her', they: 'them' };

/**
 * The pronoun antecedents the parser should read ahead of recency right now.
 *
 * @param bundle - The loaded bundle
 * @param state - The live state
 * @returns The partner's pronoun naming the partner, or `undefined` when there is no partner in earshot with a pronoun
 */
export const pronounAntecedentsOf = (bundle: EngineGameBundle, state: RuntimeState): PronounAntecedents | undefined => {
	const partnerId = state.session.conversationPartnerId;

	if (partnerId === undefined) {
		return undefined;
	}

	const { pronoun } = requireActor(bundle, partnerId);

	if (pronoun === undefined || !isActorAddressable(bundle, state, partnerId)) {
		return undefined;
	}

	return { [OBJECT_FORM_OF[pronoun]]: entityRefForActor(partnerId) };
};

/**
 * The catalog with the partner's pronoun reserved for the partner: the word is
 * removed from every OTHER entry's nouns and aliases for this parse, so the
 * declared-word reading cannot take it, and the recency view (headed by the
 * partner) decides. An entry that word was the whole name of — a topic WORD
 * (`her` among the keys about EURYDICE) — is left out of this parse: while
 * she is being talked with, the word is hers, and comes back when she is not.
 *
 * @param catalog - The catalog (or topic catalog) as derived for this parse
 * @param antecedents - The partner's antecedent map, from `pronounAntecedentsOf`
 * @returns The catalog, with the partner's pronoun its own; the same catalog when there is nothing to reserve
 */
export const withPartnerPronounReserved = (
	catalog: readonly ParseCandidateEntity[],
	antecedents: PronounAntecedents | undefined,
): readonly ParseCandidateEntity[] => {
	if (antecedents === undefined) {
		return catalog;
	}

	const reserved = new Set(Object.keys(antecedents));
	const partnerRefs = new Set(Object.values(antecedents));
	const isReserved = (word: string): boolean => reserved.has(word);

	return catalog.flatMap((entry) => {
		if (partnerRefs.has(entry.entityRef) || ![...entry.nouns, ...entry.aliases].some(isReserved)) {
			return [entry];
		}

		const nouns = entry.nouns.filter((noun) => !isReserved(noun));

		return nouns.length === 0 ? [] : [{ ...entry, aliases: entry.aliases.filter((alias) => !isReserved(alias)), nouns }];
	});
};
